import { X, Check } from "lucide-react";
import { offer, whatsappPrefill } from "@/config/site";
import { Section } from "./Section";
import { Reveal } from "./Reveal";

const PANELS = [
    {
        id: "ppf",
        title: "Paint Protection Film",
        without: [
            "Exposed paint takes every stone chip directly",
            "Swirl marks and light scratches accumulate in the clear coat",
            "High-impact zones age faster than the rest of the car",
        ],
        with: [
            "A self-healing film layer takes the impact instead of the paint",
            "The factory finish underneath stays preserved",
            "Protected panels keep their gloss far longer",
        ],
        image: "/assets/work/work-ppf-x5.webp",
        alt: "Real Revautoz work — paint protection film being applied to a BMW X5 bonnet",
        placeholder: true,
    },
    {
        id: "ceramic",
        title: "Ceramic Coating",
        without: [
            "Water sheets and sits on the surface, carrying dirt",
            "Contamination bonds to unprotected paint",
            "Gloss fades and washing takes more effort",
        ],
        with: [
            "Water beads and runs off a hydrophobic surface",
            "Dirt releases more easily during washing",
            "Enhanced gloss with lasting surface protection",
        ],
        image: "/assets/work/ceramic-application.webp",
        alt: "Real Revautoz work — ceramic coating being hand-applied to a black SUV",
        placeholder: true,
    },
];

export const DifferenceProof = () => (
    <Section id="the-difference" index="03" label="Protection" title="What protection actually changes.">
        <Reveal className="mt-6" delay={0.05}>
            <p className="max-w-[60ch] text-base leading-relaxed text-rz-smoke">
                Protection is easier to understand when you can see what it changes. Here's what
                each treatment is designed to do — shown, not just claimed.
            </p>
        </Reveal>
        <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {PANELS.map((p, i) => (
                <Reveal
                    key={p.id}
                    delay={i * 0.08}
                    className="rz-corners border border-rz-steel/60 bg-rz-graphite/40"
                >
                    <div className="relative overflow-hidden">
                        <img
                            src={p.image}
                            alt={p.alt}
                            loading="lazy"
                            decoding="async"
                            className="aspect-[16/8] w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-rz-graphite via-transparent to-transparent" />
                        <h3 className="absolute bottom-4 left-6 font-display text-2xl font-bold uppercase tracking-wide text-rz-white">
                            {p.title}
                        </h3>
                    </div>
                    <div className="grid gap-6 p-6 sm:grid-cols-2 sm:p-8">
                        <div>
                            <p className="font-display text-[10px] uppercase tracking-[0.22em] text-rz-smoke">
                                Without Revautoz
                            </p>
                            <ul className="mt-3 space-y-2.5">
                                {p.without.map((t) => (
                                    <li key={t} className="flex gap-2.5 text-[13px] leading-relaxed text-rz-smoke">
                                        <X size={13} className="mt-1 shrink-0 text-rz-smoke/70" />
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <p className="font-display text-[10px] uppercase tracking-[0.22em] text-rz-red">
                                With Revautoz
                            </p>
                            <ul className="mt-3 space-y-2.5">
                                {p.with.map((t) => (
                                    <li key={t} className="flex gap-2.5 text-[13px] leading-relaxed text-rz-white/90">
                                        <Check size={13} className="mt-1 shrink-0 text-rz-red" />
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Reveal>
            ))}
        </div>
        <Reveal className="mt-8" delay={0.1}>
            <a
                data-testid="difference-inspection-cta"
                href={whatsappPrefill(offer.message)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-display text-[12px] font-semibold uppercase tracking-[0.2em] text-rz-red transition-transform duration-300 hover:translate-x-1"
            >
                See what your car needs — {offer.cta} →
            </a>
        </Reveal>
    </Section>
);
